import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { sponsorApi } from "../api/sponsorApi";
import { InvoiceWhatsAppActions } from "../components/InvoiceWhatsAppActions";
import "../events.css";
import "./sponsor.css";

function useQuery() {
  var loc = useLocation();
  return useMemo(function () {
    return new URLSearchParams(loc.search || "");
  }, [loc.search]);
}

function fmtInr(val) {
  var n = parseFloat(val);
  if (isNaN(n)) return "₹0";
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function fmtEventDate(val) {
  if (!val) return "";
  var d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

var HOW_IT_WORKS = [
  { n: "1", title: "Pick a package", desc: "Choose the association or stall option that fits your brand." },
  { n: "2", title: "Share your details", desc: "Company, committee, contact and the signed MoU." },
  { n: "3", title: "Pay or submit", desc: "Pay by Razorpay UPI QR or Cashfree, or record an offline transfer for our team to verify." },
];

function emptyForm() {
  return {
    company_name: "",
    committee_id: "",
    package_code: "",
    agreed_amount: "",
    payment_mode: "",
    transaction_reference: "",
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    notes: "",
    signed_mou: null,
    brand_logo: null,
    agreed: false,
  };
}

function ensureCashfree() {
  return new Promise(function (resolve, reject) {
    if (window.Cashfree) return resolve(window.Cashfree);
    var existing = document.querySelector('script[src*="cashfree"]');
    if (existing) {
      existing.addEventListener("load", function () { resolve(window.Cashfree); });
      existing.addEventListener("error", function () { reject(new Error("Payment SDK unavailable.")); });
      return;
    }
    var s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.onload = function () { resolve(window.Cashfree); };
    s.onerror = function () { reject(new Error("Payment SDK unavailable.")); };
    document.body.appendChild(s);
  });
}

function launchCashfree(sessionId) {
  return ensureCashfree().then(function (Cashfree) {
    if (!Cashfree) throw new Error("Payment SDK unavailable.");
    var mode = process.env.REACT_APP_CASHFREE_ENV === "sandbox" ? "sandbox" : "production";
    var cf = Cashfree({ mode: mode });
    return cf.checkout({
      paymentSessionId: sessionId,
      redirectTarget: "_modal",
      appearance: { width: "425px", height: "700px" },
    });
  });
}

const SponsorFormPage = function () {
  var params = useParams();
  var qs = useQuery();
  var eventKey = params.eventSlug || "zephyr-2026";
  var ref = qs.get("ref") || "";

  var _boot = useState({ loading: true, error: "", data: null });
  var boot = _boot[0], setBoot = _boot[1];
  var _form = useState(emptyForm());
  var form = _form[0], setForm = _form[1];
  var _status = useState({ busy: false, error: "", result: null });
  var status = _status[0], setStatus = _status[1];
  var _logoUrl = useState("");
  var logoUrl = _logoUrl[0], setLogoUrl = _logoUrl[1];
  var _cfSession = useState("");
  var cfSession = _cfSession[0], setCfSession = _cfSession[1];

  useEffect(function () {
    document.title = "Sponsor ZEPHYR '26 | ParkAdda Events";
    var alive = true;
    sponsorApi.bootstrap(eventKey)
      .then(function (payload) {
        if (alive) setBoot({ loading: false, error: "", data: payload });
      })
      .catch(function (e) {
        if (alive) setBoot({ loading: false, error: e.message || "Unable to load sponsorship options.", data: null });
      });
    return function () { alive = false; };
  }, [eventKey]);

  useEffect(function () {
    if (!ref) return;
    var alive = true;
    sponsorApi.lookup(ref)
      .then(function (payload) {
        if (alive) setStatus({ busy: false, error: "", result: payload });
      })
      .catch(function () {});
    return function () { alive = false; };
  }, [ref]);

  useEffect(function () {
    return function () {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    };
  }, [logoUrl]);

  useEffect(function () {
    var current = status.result;
    if (!current || !current.sponsor_id) return;
    if (current.payment_status === "PAID" || current.payment_status === "FAILED") return;
    var mode = current.payment_mode;
    var qr = current.upi_qr;
    var waitingGateway = mode === "UPI" || mode === "CASHFREE_PG" ||
      current.latest_payment_status === "INITIATED" ||
      (qr && (qr.image_url || qr.qrcode_id));
    if (!waitingGateway) return;
    var alive = true;
    var timer = setInterval(function () {
      sponsorApi.lookup(current.sponsor_id).then(function (payload) {
        if (!alive || !payload) return;
        setStatus(function (prev) {
          return Object.assign({}, prev, { result: payload, error: "" });
        });
      }).catch(function () {});
    }, 4000);
    return function () {
      alive = false;
      clearInterval(timer);
    };
  }, [status.result && status.result.sponsor_id, status.result && status.result.payment_status]);

  var packages = (boot.data && boot.data.packages) || [];
  var committees = (boot.data && boot.data.committees) || [];
  var modes = (boot.data && boot.data.payment_modes) || [];
  var selectedPkg = packages.find(function (p) { return p.code === form.package_code; }) || null;
  var isOnline = form.payment_mode === "CASHFREE_PG";
  var isUpi = form.payment_mode === "UPI";
  var isInstantPay = isOnline || isUpi;

  var packageGroups = useMemo(function () {
    var stalls = [];
    var association = [];
    packages.forEach(function (pkg) {
      if (pkg.category === "STALL") stalls.push(pkg);
      else association.push(pkg);
    });
    var byOrder = function (a, b) { return (a.display_order || 0) - (b.display_order || 0); };
    var groups = [];
    if (association.length) {
      groups.push({
        key: "association",
        label: "Brand association",
        hint: "Get your brand on fest branding and communication.",
        items: association.sort(byOrder),
      });
    }
    if (stalls.length) {
      groups.push({
        key: "stalls",
        label: "On-ground stalls",
        hint: "Reserve physical space to meet students face to face.",
        items: stalls.sort(byOrder),
      });
    }
    return groups;
  }, [packages]);

  var setField = function (field) {
    return function (e) {
      var value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm(function (prev) {
        var next = Object.assign({}, prev);
        next[field] = value;
        return next;
      });
      setStatus(function (prev) { return Object.assign({}, prev, { error: "" }); });
    };
  };

  var onPaymentMode = function (e) {
    var value = e.target.value;
    setForm(function (prev) {
      return Object.assign({}, prev, {
        payment_mode: value,
        transaction_reference: (value === "CASHFREE_PG" || value === "UPI") ? "" : prev.transaction_reference,
      });
    });
    setStatus(function (prev) { return Object.assign({}, prev, { error: "" }); });
  };

  /* No amount is pre-filled: the sponsor types what they are committing. */
  var selectPackage = function (pkg) {
    setForm(function (prev) {
      return Object.assign({}, prev, { package_code: pkg.code });
    });
    setStatus(function (prev) { return Object.assign({}, prev, { error: "" }); });
  };

  var onMou = function (e) {
    var file = (e.target.files && e.target.files[0]) || null;
    setForm(function (prev) { return Object.assign({}, prev, { signed_mou: file }); });
  };

  var onLogo = function (e) {
    var file = (e.target.files && e.target.files[0]) || null;
    setForm(function (prev) { return Object.assign({}, prev, { brand_logo: file }); });
    if (logoUrl) URL.revokeObjectURL(logoUrl);
    setLogoUrl(file ? URL.createObjectURL(file) : "");
  };

  var validate = function () {
    if (!form.company_name.trim()) return "Company name is required.";
    if (!form.committee_id) return "Select a committee.";
    if (!form.package_code || !selectedPkg) return "Select a sponsorship category.";
    var rawAmount = String(form.agreed_amount).replace(/,/g, "").trim();
    var amount = Number(rawAmount);
    /* Number() rather than parseFloat so "500abc" is rejected instead of read as 500. */
    if (!rawAmount || !isFinite(amount)) return "Enter a valid sponsorship amount.";
    if (amount <= 0) return "Sponsorship amount must be greater than zero.";
    if (!form.payment_mode) return "Select a payment mode.";
    if (!form.signed_mou) return "Please upload a PDF, JPG or PNG file.";
    if (form.signed_mou.size > 10 * 1024 * 1024) return "MoU file cannot exceed 10 MB.";
    if (form.brand_logo && form.brand_logo.size > 5 * 1024 * 1024) return "Brand logo cannot exceed 5 MB.";
    if (form.contact_phone && !/^[6-9]\d{9}$/.test(form.contact_phone.replace(/\s/g, ""))) {
      return "Enter a valid 10-digit Indian mobile number.";
    }
    if (form.contact_email && form.contact_email.indexOf("@") < 0) return "Enter a valid email address.";
    if (!form.agreed) return "Please confirm the sponsorship details before submitting.";
    return "";
  };

  var onSubmit = function (e) {
    e.preventDefault();
    if (status.busy) return;
    var err = validate();
    if (err) {
      setStatus({ busy: false, error: err, result: null });
      return;
    }
    var fd = new FormData();
    fd.append("company_name", form.company_name.trim());
    fd.append("committee_id", form.committee_id);
    fd.append("package_code", form.package_code);
    fd.append("agreed_amount", String(form.agreed_amount).replace(/,/g, ""));
    fd.append("payment_mode", form.payment_mode);
    fd.append("transaction_reference", form.transaction_reference.trim());
    fd.append("contact_person", form.contact_person.trim());
    fd.append("contact_phone", form.contact_phone.trim());
    fd.append("contact_email", form.contact_email.trim());
    fd.append("notes", form.notes.trim());
    fd.append("signed_mou", form.signed_mou);
    if (form.brand_logo) fd.append("brand_logo", form.brand_logo);

    setStatus({ busy: true, error: "", result: null });
    sponsorApi.submit(eventKey, fd)
      .then(function (payload) {
        if (payload && payload.sponsor_id) {
          try {
            window.history.replaceState({}, "", "?ref=" + encodeURIComponent(payload.sponsor_id));
          } catch (_) {}
        }
        var session = payload && payload.payment_session;
        if (session && session.payment_session_id) {
          setCfSession(session.payment_session_id);
          return launchCashfree(session.payment_session_id).then(function () {
            return sponsorApi.lookup(payload.sponsor_id).catch(function () { return payload; });
          }).catch(function () {
            return payload;
          });
        }
        return payload;
      })
      .then(function (payload) {
        setStatus({ busy: false, error: "", result: payload });
        window.scrollTo(0, 0);
      })
      .catch(function (ex) {
        setStatus({
          busy: false,
          error: ex.message || "Unable to submit sponsorship details. Please try again.",
          result: null,
        });
      });
  };

  var eventMeta = (boot.data && boot.data.event) || {};
  var eventTitle = eventMeta.title || "ZEPHYR '26";
  var backHref = "/events/view?event_id=" + (eventMeta.event_id || "ZEPHYR26");
  var venueLine = [eventMeta.venue_name, eventMeta.city].filter(Boolean).join(", ");
  var dateLine = [fmtEventDate(eventMeta.start_datetime), fmtEventDate(eventMeta.end_datetime)].filter(Boolean).join(" – ");
  var result = status.result;

  var pageShell = function (inner) {
    return (
      <div className="ev-page sp-page">
        <div className="ev-container">
          <div className="sp-wrap">
            <div className="bk-header">
              <Link to={backHref} className="bk-back-link">Back to {eventTitle}</Link>
            </div>
            {inner}
          </div>
        </div>
      </div>
    );
  };

  if (boot.loading) {
    return pageShell(
      <div className="bk-loader-wrap">
        <div className="bk-spinner" />
        <p>Loading sponsorship options…</p>
      </div>
    );
  }
  if (boot.error) {
    return pageShell(
      <div className="sp-card">
        <h2 className="sp-card__title">Unable to open the sponsor portal</h2>
        <p className="sp-card__desc">{boot.error}</p>
        <div className="sp-actions">
          <Link to="/events" className="bk-btn bk-btn--primary">Back to events</Link>
        </div>
      </div>
    );
  }

  if (result) {
    var paid = result.payment_status === "PAID";
    var failed = result.payment_status === "FAILED";
    var upiQr = result.upi_qr || (result.payment && result.payment.upi_qr) || null;
    var waitingUpi = !paid && !failed && result.payment_mode === "UPI";
    var waitingCashfree = !paid && !failed && result.payment_mode === "CASHFREE_PG";
    var awaitingPay = waitingUpi || waitingCashfree;
    var refreshStatus = function () {
      if (!result.sponsor_id || status.busy) return;
      setStatus(function (prev) { return Object.assign({}, prev, { busy: true, error: "" }); });
      sponsorApi.lookup(result.sponsor_id)
        .then(function (payload) {
          setStatus({ busy: false, error: "", result: payload });
        })
        .catch(function (ex) {
          setStatus(function (prev) {
            return Object.assign({}, prev, { busy: false, error: ex.message || "Unable to refresh payment status." });
          });
        });
    };
    var reopenCashfree = function () {
      if (!cfSession || status.busy) return;
      setStatus(function (prev) { return Object.assign({}, prev, { busy: true, error: "" }); });
      launchCashfree(cfSession)
        .then(function () { return sponsorApi.lookup(result.sponsor_id); })
        .then(function (payload) {
          setStatus({ busy: false, error: "", result: payload });
        })
        .catch(function (ex) {
          setStatus(function (prev) {
            return Object.assign({}, prev, { busy: false, error: ex.message || "Payment window closed. Check status after you pay." });
          });
        });
    };
    return pageShell(
      <React.Fragment>
        <div className={"sp-receipt" + (paid ? " sp-receipt--paid" : "") + (failed ? " sp-receipt--failed" : "")}>
          <span className="sp-receipt__mark" aria-hidden="true">{paid ? "✓" : (failed ? "!" : "⏳")}</span>
          <p className="sp-receipt__kicker">{eventTitle} · Sponsorship</p>
          <h1 className="sp-receipt__title">
            {paid
              ? "Sponsorship confirmed"
              : (failed
                ? "Payment failed"
                : (waitingUpi ? "Scan to pay" : (waitingCashfree ? "Complete payment" : "Details received")))}
          </h1>
          <p className="sp-receipt__sub">
            {paid
              ? "Payment is verified. Thank you for partnering with ZEPHYR '26. Keep this acknowledgement for your records."
              : (failed
                ? "The payment did not go through. Try again or use another payment mode."
                : (waitingUpi
                  ? "Scan the Razorpay UPI QR below. The acknowledgement appears only after payment is confirmed."
                  : (waitingCashfree
                    ? "Finish Cashfree checkout. The acknowledgement appears only after payment is confirmed."
                    : "Your details are saved. This stays pending until the ZEPHYR finance team verifies the transfer.")))}
          </p>
          <span className={"sp-badge " + (paid ? "sp-badge--paid" : (failed ? "sp-badge--failed" : "sp-badge--pending"))}>
            {paid ? "Paid & verified" : (failed ? "Payment failed" : (awaitingPay ? "Awaiting payment" : "Pending verification"))}
          </span>
        </div>

        {waitingUpi ? (
          <div className="sp-card sp-paywall">
            <div className="sp-card__head">
              <h2 className="sp-card__title">Razorpay UPI QR</h2>
              <p className="sp-card__desc">
                Pay {fmtInr(upiQr.amount || result.agreed_amount)} using GPay, PhonePe, Paytm or any UPI app.
              </p>
            </div>
            {upiQr.image_url ? (
              <img className="sp-qr__img" src={upiQr.image_url} alt="Razorpay UPI QR code" />
            ) : (
              <p className="sp-note">QR image is generating. Use Check status in a few seconds.</p>
            )}
            <p className="sp-qr__hint">Do not close this page. Confirmation is automatic after a successful scan.</p>
            <div className="sp-actions">
              <button type="button" className="bk-btn bk-btn--primary" onClick={refreshStatus} disabled={status.busy}>
                {status.busy ? "Checking…" : "Check payment status"}
              </button>
            </div>
            {status.error ? <p className="sp-error" role="alert">{status.error}</p> : null}
          </div>
        ) : null}

        {waitingCashfree ? (
          <div className="sp-card sp-paywall">
            <div className="sp-card__head">
              <h2 className="sp-card__title">Cashfree checkout</h2>
              <p className="sp-card__desc">
                Pay {fmtInr(result.agreed_amount)} in the secure Cashfree window. This page updates when the payment is verified.
              </p>
            </div>
            <p className="sp-qr__hint">If you closed the window, open it again and complete the payment.</p>
            <div className="sp-actions">
              {cfSession ? (
                <button type="button" className="bk-btn bk-btn--primary" onClick={reopenCashfree} disabled={status.busy}>
                  {status.busy ? "Opening…" : "Open Cashfree again"}
                </button>
              ) : null}
              <button type="button" className="bk-btn bk-btn--ghost" onClick={refreshStatus} disabled={status.busy}>
                {status.busy ? "Checking…" : "Check payment status"}
              </button>
            </div>
            {status.error ? <p className="sp-error" role="alert">{status.error}</p> : null}
          </div>
        ) : null}

        {paid ? (
          <div className="sp-card">
            <div className="sp-card__head">
              <h2 className="sp-card__title">Acknowledgement</h2>
              <p className="sp-card__desc">
                Your invoice is below. We have also sent it to your WhatsApp
                {result.contact_email ? " and email address" : ""}.
                Quote the reference in any correspondence with the ZEPHYR team.
              </p>
            </div>
            <dl className="sp-success__rows">
              <dt>Reference</dt><dd className="sp-success__ref">{result.sponsor_id}</dd>
              <dt>Company</dt><dd>{result.company_name}</dd>
              <dt>Category</dt><dd>{result.category_label || result.package_name}</dd>
              {result.committee_label ? (
                <React.Fragment>
                  <dt>Committee</dt><dd>{result.committee_label}</dd>
                </React.Fragment>
              ) : null}
              <dt>Sponsorship amount</dt><dd>{fmtInr(result.agreed_amount)}</dd>
              <dt>Amount paid</dt><dd>{fmtInr(result.total_paid)}</dd>
              <dt>Status</dt><dd>Paid &amp; verified</dd>
            </dl>
            <InvoiceWhatsAppActions
              kind="sponsor"
              bookingId={result.sponsor_id}
              paid={true}
              phone={result.contact_phone}
              className="sp-actions"
              primaryClass="bk-btn bk-btn--primary"
              ghostClass="bk-btn bk-btn--ghost"
            />
            <div className="sp-actions">
              <button type="button" className="bk-btn bk-btn--ghost" onClick={function () { window.print(); }}>
                Print this page
              </button>
              <Link to={backHref} className="bk-btn bk-btn--ghost">Return to {eventTitle}</Link>
            </div>
          </div>
        ) : null}

        {!awaitingPay && !paid ? (
          <div className="sp-card">
            <div className="sp-card__head">
              <h2 className="sp-card__title">{failed ? "Try another payment" : "Saved for verification"}</h2>
              <p className="sp-card__desc">
                {failed
                  ? "No acknowledgement is issued until a successful payment is confirmed."
                  : "Offline transfers are not treated as paid until the ZEPHYR finance team verifies them."}
              </p>
            </div>
            <dl className="sp-success__rows">
              <dt>Reference</dt><dd className="sp-success__ref">{result.sponsor_id}</dd>
              <dt>Company</dt><dd>{result.company_name}</dd>
              <dt>Amount</dt><dd>{fmtInr(result.agreed_amount)}</dd>
            </dl>
            <div className="sp-actions">
              <Link to={backHref} className="bk-btn bk-btn--ghost">Return to {eventTitle}</Link>
            </div>
          </div>
        ) : null}
      </React.Fragment>
    );
  }

  return pageShell(
    <React.Fragment>
      <div className="sp-hero">
        <p className="sp-hero__kicker">{eventTitle} · Sponsorship</p>
        <h1 className="sp-hero__title">Put your brand at the centre of the fest</h1>
        <p className="sp-hero__sub">
          Choose a stall or association package, tell us the amount you would like to
          contribute, and pay online or offline.
        </p>
        <div className="sp-hero__meta">
          {venueLine ? <span><strong>Venue</strong>{venueLine}</span> : null}
          {dateLine ? <span><strong>Dates</strong>{dateLine}</span> : null}
          {packages.length ? <span><strong>Packages</strong>{packages.length}</span> : null}
          {committees.length ? <span><strong>Committees</strong>{committees.length}</span> : null}
        </div>
      </div>

      <ol className="sp-steps">
        {HOW_IT_WORKS.map(function (step) {
          return (
            <li className="sp-step" key={step.n}>
              <span className="sp-step__num" aria-hidden="true">{step.n}</span>
              <div>
                <p className="sp-step__title">{step.title}</p>
                <p className="sp-step__desc">{step.desc}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <form className="sp-form" onSubmit={onSubmit}>
        <div className="sp-card">
          <div className="sp-card__head">
            <span className="sp-card__step">Step 1</span>
            <h2 className="sp-card__title">Choose your sponsorship</h2>
            <p className="sp-card__desc">Select one package, then enter the amount you wish to sponsor.</p>
          </div>

          {packageGroups.map(function (group) {
            return (
              <div className="sp-pkg-group" key={group.key}>
                <div className="sp-pkg-group__head">
                  <h3 className="sp-pkg-group__label">{group.label}</h3>
                  <p className="sp-pkg-group__hint">{group.hint}</p>
                </div>
                <div className="sp-grid sp-grid--packages">
                  {group.items.map(function (pkg) {
                    var active = form.package_code === pkg.code;
                    return (
                      <button
                        type="button"
                        key={pkg.code}
                        className={"sp-pkg" + (active ? " is-active" : "")}
                        aria-pressed={active}
                        onClick={function () { selectPackage(pkg); }}
                      >
                        <span className="sp-pkg__check" aria-hidden="true">✓</span>
                        <p className="sp-pkg__name">{pkg.display_name}</p>
                        <p className="sp-pkg__desc">{pkg.description}</p>
                        {(pkg.benefits || []).length ? (
                          <ul className="sp-pkg__benefits">
                            {pkg.benefits.slice(0, 4).map(function (b) {
                              return <li key={b}>{b}</li>;
                            })}
                          </ul>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {selectedPkg ? (
            <div className="sp-amount">
              <div className="sp-field">
                <label htmlFor="sp-amount">
                  Your sponsorship amount <span className="sp-req">*</span>
                </label>
                <div className="sp-amount__input">
                  <span className="sp-amount__prefix" aria-hidden="true">₹</span>
                  <input
                    id="sp-amount"
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    placeholder="Enter amount"
                    value={form.agreed_amount}
                    onChange={setField("agreed_amount")}
                    required
                  />
                </div>
                <p className="sp-field__hint">
                  Enter the amount you are sponsoring for {selectedPkg.display_name}.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="sp-card">
          <div className="sp-card__head">
            <span className="sp-card__step">Step 2</span>
            <h2 className="sp-card__title">Company &amp; committee</h2>
            <p className="sp-card__desc">The committee you are coordinating this sponsorship with.</p>
          </div>
          <div className="sp-grid">
            <div className="sp-field sp-field--full">
              <label htmlFor="sp-company">Company name <span className="sp-req">*</span></label>
              <input id="sp-company" maxLength={255} value={form.company_name} onChange={setField("company_name")} required />
            </div>
            <div className="sp-field sp-field--full">
              <label htmlFor="sp-committee">Committee <span className="sp-req">*</span></label>
              <select id="sp-committee" value={form.committee_id} onChange={setField("committee_id")} required>
                <option value="">Select committee</option>
                {committees.map(function (c) {
                  return <option key={c.id} value={c.id}>{c.label || c.name || c.code}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card__head">
            <span className="sp-card__step">Step 3</span>
            <h2 className="sp-card__title">Point of contact</h2>
            <p className="sp-card__desc">Optional, but it helps us reach the right person quickly.</p>
          </div>
          <div className="sp-grid sp-grid--3">
            <div className="sp-field">
              <label htmlFor="sp-person">Contact person</label>
              <input id="sp-person" value={form.contact_person} onChange={setField("contact_person")} autoComplete="name" />
            </div>
            <div className="sp-field">
              <label htmlFor="sp-phone">Mobile number</label>
              <input id="sp-phone" inputMode="numeric" maxLength={10} value={form.contact_phone} onChange={setField("contact_phone")} autoComplete="tel" />
            </div>
            <div className="sp-field">
              <label htmlFor="sp-email">Email</label>
              <input id="sp-email" type="email" value={form.contact_email} onChange={setField("contact_email")} autoComplete="email" />
            </div>
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card__head">
            <span className="sp-card__step">Step 4</span>
            <h2 className="sp-card__title">Payment</h2>
            <p className="sp-card__desc">Pay online now, or record an offline transfer for our team to verify.</p>
          </div>
          <div className="sp-grid">
            <div className="sp-field">
              <label htmlFor="sp-mode">Payment mode <span className="sp-req">*</span></label>
              <select id="sp-mode" value={form.payment_mode} onChange={onPaymentMode} required>
                <option value="">Select payment mode</option>
                {modes.map(function (m) {
                  return (
                    <option key={m.code} value={m.code}>
                      {m.code === "CASHFREE_PG" ? "Online payment (Cashfree)" : (m.code === "UPI" ? "UPI (Razorpay QR)" : m.label)}
                    </option>
                  );
                })}
              </select>
            </div>
            {form.payment_mode && !isInstantPay ? (
              <div className="sp-field">
                <label htmlFor="sp-ref">Transaction reference</label>
                <input id="sp-ref" value={form.transaction_reference} onChange={setField("transaction_reference")} placeholder="UTR / cheque no." />
              </div>
            ) : null}
          </div>
          {isOnline ? (
            <p className="sp-note">
              You will be redirected to secure Cashfree checkout right after submitting. No reference number needed.
            </p>
          ) : null}
          {isUpi ? (
            <p className="sp-note">
              After submitting you will see a <strong>Razorpay UPI QR</strong>. Scan it from any UPI app. Payment confirms automatically.
            </p>
          ) : null}
          {form.payment_mode && !isInstantPay ? (
            <p className="sp-note">
              Offline payments stay <strong>pending</strong> until the ZEPHYR finance team verifies them.
            </p>
          ) : null}
        </div>

        <div className="sp-card">
          <div className="sp-card__head">
            <span className="sp-card__step">Step 5</span>
            <h2 className="sp-card__title">Documents</h2>
            <p className="sp-card__desc">The signed MoU is required. A logo helps us get your branding right.</p>
          </div>
          <div className="sp-grid">
            <div className="sp-field">
              <label htmlFor="sp-mou">Signed MoU <span className="sp-req">*</span></label>
              <label className={"sp-drop" + (form.signed_mou ? " is-filled" : "")}>
                <span className="sp-drop__icon" aria-hidden="true">{form.signed_mou ? "📄" : "⬆"}</span>
                <span className="sp-drop__text">
                  <span className="sp-drop__title">
                    {form.signed_mou ? form.signed_mou.name : "Choose a file"}
                  </span>
                  <span className="sp-drop__hint">PDF, JPG or PNG · max 10 MB</span>
                </span>
                <input
                  id="sp-mou"
                  className="sp-drop__input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={onMou}
                />
              </label>
            </div>
            <div className="sp-field">
              <label htmlFor="sp-logo">Company / brand logo</label>
              <label className={"sp-drop" + (form.brand_logo ? " is-filled" : "")}>
                {logoUrl
                  ? <img className="sp-drop__preview" src={logoUrl} alt="Logo preview" />
                  : <span className="sp-drop__icon" aria-hidden="true">🖼</span>}
                <span className="sp-drop__text">
                  <span className="sp-drop__title">
                    {form.brand_logo ? form.brand_logo.name : "Choose a file"}
                  </span>
                  <span className="sp-drop__hint">PNG, JPG or WEBP · max 5 MB</span>
                </span>
                <input
                  id="sp-logo"
                  className="sp-drop__input"
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                  onChange={onLogo}
                />
              </label>
            </div>
            <div className="sp-field sp-field--full">
              <label htmlFor="sp-notes">Remarks / notes</label>
              <textarea
                id="sp-notes"
                value={form.notes}
                onChange={setField("notes")}
                placeholder="Anything the ZEPHYR team should know — stall preferences, branding needs, billing details."
              />
            </div>
          </div>
        </div>

        <div className="sp-card">
          <label className="sp-check">
            <input type="checkbox" checked={form.agreed} onChange={setField("agreed")} />
            <span>I confirm that the company, committee, amount and documents above are accurate for this ZEPHYR '26 sponsorship.</span>
          </label>
          {status.error ? <p className="sp-error" role="alert">{status.error}</p> : null}
        </div>

        <div className="sp-summary">
          <div className="sp-summary__info">
            <span className="sp-summary__label">
              {selectedPkg ? selectedPkg.display_name : "No package selected"}
            </span>
            <span className="sp-summary__value">
              {selectedPkg && form.agreed_amount ? fmtInr(form.agreed_amount) : "—"}
            </span>
          </div>
          <button type="submit" className="bk-btn bk-btn--primary bk-btn--lg sp-summary__cta" disabled={status.busy}>
            {status.busy ? "Submitting…" : (isInstantPay ? "Submit & pay securely" : "Submit sponsorship")}
          </button>
        </div>
      </form>
    </React.Fragment>
  );
};

export default SponsorFormPage;
